import { useState } from 'react';
import { Alert, Box, Button, TextField, Typography } from '@mui/material';
import { Tip, Explain } from './explain';
import X from './explainText';

const MIRROR_SYSTEM = { ado: 'DevOps', monday: 'Monday' };

const defaultWhen = (iso) => {
    const d = iso ? new Date(iso) : null;
    return d && !Number.isNaN(d.getTime())
        ? d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
        : '';
};

/**
 * One note's text, with what ADO #4318 added to every note in the portal:
 * an Edit action on your own notes, "edited <when> by <who>", the earlier
 * versions on request, and -- for DevOps and Monday -- a visible state when
 * the copy in that system was not updated.
 *
 * `note` is the normalised shape from workService (normaliseWorkNote /
 * normaliseTodoNote / normaliseRequestNote): { text, mine, editedAt,
 * editedBy, history, mirror }. `mine` is the server's answer, never decided
 * here. `onSave(text)` performs the edit; a rejection is shown, and the
 * editor stays open with what was typed.
 *
 * The host keeps its own author/date line, so each place notes appear keeps
 * its look: above the note, or passed as `byline` to sit under the text.
 * `nameOf` and `when` format names and times the host's way.
 */
export default function EditableNote({
    note, onSave, source = '', nameOf = (x) => x, when = defaultWhen,
    textSx = {}, metaSx = {}, maxLength, byline = null,
}) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [showHistory, setShowHistory] = useState(false);

    const meta = { fontSize: 11, color: 'var(--color-text-secondary, var(--co-txt3))', ...metaSx };
    const edited = !!note.editedAt;
    const history = note.history;
    const system = MIRROR_SYSTEM[source];

    const start = () => { setDraft(note.text); setError(null); setEditing(true); };
    const save = async () => {
        const text = draft.trim();
        if (!text) return;
        if (text === note.text) { setEditing(false); return; }
        setBusy(true);
        setError(null);
        try {
            await onSave(text);
            setEditing(false);
        } catch (e) { setError(e.message || String(e)); } finally { setBusy(false); }
    };

    if (editing) {
        return (
            <Box data-testid="note-editor" onClick={(e) => e.stopPropagation()}>
                <TextField size="small" fullWidth multiline minRows={2} maxRows={8} autoFocus
                    value={draft} onChange={(e) => setDraft(e.target.value)}
                    inputProps={{ 'aria-label': 'Edit note', ...(maxLength ? { maxLength } : {}) }}
                    onKeyDown={(e) => {
                        // Stop the host's own key handling (a Popover's or a row's).
                        e.stopPropagation();
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); save(); }
                        if (e.key === 'Escape') setEditing(false);
                    }}
                    sx={{ '& textarea': { fontSize: 12.5 } }} />
                <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end', mt: 0.5 }}>
                    <Button size="small" disabled={busy} onClick={() => setEditing(false)}
                        sx={{ textTransform: 'none', fontSize: 11.5 }}>
                        Cancel
                    </Button>
                    <Button size="small" variant="contained" disabled={busy || !draft.trim()} onClick={save}
                        sx={{ textTransform: 'none', fontSize: 11.5 }}>
                        Save
                    </Button>
                </Box>
                {error && <Alert severity="error" sx={{ fontSize: 11.5, mt: 0.5 }}>The note was not edited: {error}</Alert>}
            </Box>
        );
    }

    return (
        <Box data-testid="editable-note">
            <Typography sx={{ fontSize: 12.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', ...textSx }}>
                {note.text}
            </Typography>
            {byline}
            {(edited || note.mine) && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1, ...meta }}>
                    {edited && (
                        <Explain text={X.noteEdit}>
                            <span data-testid="note-edited">
                                edited {when(note.editedAt)}{note.editedBy ? ` by ${nameOf(note.editedBy)}` : ''}
                            </span>
                        </Explain>
                    )}
                    {edited && history === null && (
                        <span data-testid="note-history-unavailable">couldn’t read earlier versions</span>
                    )}
                    {history && history.length > 0 && (
                        <Box component="button" type="button" data-testid="note-history-toggle"
                            onClick={(e) => { e.stopPropagation(); setShowHistory((v) => !v); }}
                            sx={{ all: 'unset', cursor: 'pointer', textDecoration: 'underline', ...meta }}>
                            {showHistory ? 'hide' : 'earlier versions'} ({history.length})
                        </Box>
                    )}
                    {note.mine && (
                        <Tip title="Edit your note" explain={X.noteEdit}>
                            <Box component="button" type="button" aria-label="Edit my note"
                                onClick={(e) => { e.stopPropagation(); start(); }}
                                sx={{ all: 'unset', cursor: 'pointer', fontWeight: 600, ...meta,
                                    color: 'var(--color-primary, #3987e5)' }}>
                                Edit
                            </Box>
                        </Tip>
                    )}
                </Box>
            )}
            {note.mirror === 'failed' && system && (
                <Explain text={X.noteMirror(system)}>
                    <Typography data-testid="note-mirror-failed"
                        sx={{ fontSize: 11, color: 'var(--color-warning, #b26a00)' }}>
                        Not updated in {system}: the copy there still shows the earlier text.
                    </Typography>
                </Explain>
            )}
            {showHistory && history && (
                <Box data-testid="note-history" sx={{ mt: 0.5, pl: 1, borderLeft: '2px dashed var(--color-border, #d0d7e2)' }}>
                    {history.map((h, i) => (
                        <Box key={`${h.at}-${i}`} sx={{ mb: 0.5 }}>
                            <Typography sx={{ fontSize: 12, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
                                color: 'var(--color-text-secondary, var(--co-txt3))' }}>
                                {h.text}
                            </Typography>
                            <Typography sx={meta}>{nameOf(h.by) || 'unknown'} · {when(h.at)}</Typography>
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
}
